/**
 * Folder-tree rendering of workspaces and their sessions for the admin
 * sidebar. Each workspace renders as a row with a chevron toggle and a
 * name button. Clicking the chevron toggles expand/collapse without
 * changing the active workspace; clicking the name promotes the
 * workspace to active and expands it. Sessions matched by `brand_id`
 * appear nested below an expanded folder.
 *
 * The active workspace is auto-expanded on mount so the surface the
 * user is currently working with is visible without an extra click.
 *
 * @module components/admin/SidebarWorkspaceTree
 */

import { Skeleton } from '../ui/Skeleton';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconChevronRight, IconSettings } from '@tabler/icons-react';
import { PlusIcon } from '@/components/icons';
import { clsx } from 'clsx';
import { useOrg } from '../../lib/OrgProvider';
import type { Workspace } from '../../lib/org-types';
import { workspaceAssetUrl } from '../workspace/assetUrl';
import { CreateWorkspaceModal } from '../workspace/CreateWorkspaceModal';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import { AnimatedLabel } from '../ui/AnimatedLabel';

/** Shape the sidebar tree needs from each session. `brand_id` may be
 *  null for legacy rows that haven't been moved into a workspace; those
 *  rows are rendered under an "Unassigned" pseudo-folder so they remain
 *  reachable. */
export interface SidebarTreeSession {
  id: string;
  title: string;
  date: string;
  brand_id?: string | null;
}

interface SidebarWorkspaceTreeProps {
  recentSessions: SidebarTreeSession[];
  onSessionClick?: (sessionId: string) => void;
  loading?: boolean;
  fetchError?: boolean;
  /** Currently selected session id (for highlight). */
  currentSessionId?: string;
}

const DEFAULT_COLOR = '#7c3aed';

/** Tiny visual marker for a workspace - `icon_url` preview or a color dot. */
const WorkspaceMarker: React.FC<{ workspace: Workspace; size?: number }> = ({
  workspace,
  size = 14,
}) => {
  const iconSrc = workspaceAssetUrl(workspace, 'icon');
  if (iconSrc) {
    return (
      <img
        src={iconSrc}
        alt=""
        aria-hidden="true"
        className="rounded-lg object-cover shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className="rounded-full inline-block shrink-0"
      style={{
        width: size,
        height: size,
        backgroundColor: workspace.color || DEFAULT_COLOR,
      }}
    />
  );
};

export const SidebarWorkspaceTree: React.FC<SidebarWorkspaceTreeProps> = ({
  recentSessions,
  onSessionClick,
  loading = false,
  fetchError = false,
  currentSessionId,
}) => {
  const navigate = useNavigate();
  const { workspaces, activeWorkspace, setActiveWorkspaceId, hasPermission } = useOrg();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [createOpen, setCreateOpen] = useState(false);

  const canCreateWorkspace = hasPermission('brands:create');
  const canEditWorkspace = hasPermission('brands:edit');

  const handleOpenSettings = (id: string) => {
    setActiveWorkspaceId(id);
    void navigate('/admin/workspace/settings');
  };

  // Auto-expand the active workspace whenever it changes so the user
  // immediately sees the sessions in the surface they just selected.
  useEffect(() => {
    if (!activeWorkspace) return;
    setExpanded((prev) => (prev[activeWorkspace.id] ? prev : { ...prev, [activeWorkspace.id]: true }));
  }, [activeWorkspace?.id]);

  if (loading) {
    return (
      <div role="status" aria-label="Loading workspace" aria-busy="true" className="space-y-4 px-2 py-2">{[0, 1, 2].map(index => <Skeleton key={index} width={index === 0 ? '72%' : '88%'} height={20} />)}</div>
    );
  }

  if (fetchError) {
    return (
      <div className="px-2 py-2 text-sm text-danger">
        Could not load your work. Please reload.
      </div>
    );
  }

  const sessionsByBrand = new Map<string, SidebarTreeSession[]>();
  const orphans: SidebarTreeSession[] = [];
  for (const s of recentSessions) {
    if (!s.brand_id) {
      orphans.push(s);
      continue;
    }
    const list = sessionsByBrand.get(s.brand_id) ?? [];
    list.push(s);
    sessionsByBrand.set(s.brand_id, list);
  }

  const handleToggle = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSelect = (id: string) => {
    setActiveWorkspaceId(id);
    setExpanded((prev) => ({ ...prev, [id]: true }));
    // Always land on the workspace's dashboard. Otherwise clicking a
    // workspace from inside an interview page silently switches scope
    // while leaving the user on an unrelated detail view.
    void navigate('/admin');
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-2 mb-2 shrink-0">
        <h3 className="text-sm font-medium text-muted-foreground">Workspaces</h3>
        {canCreateWorkspace && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            aria-label="New workspace"
            className="p-1 rounded-lg hover:bg-surface-hover text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          >
            <PlusIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-dark space-y-0.5">
        {workspaces.length === 0 ? (
          <div className="text-sm text-muted-foreground px-2 py-2">
            No workspaces yet.
          </div>
        ) : (
          workspaces.map((w) => {
            const isActive = activeWorkspace?.id === w.id;
            const isOpen = !!expanded[w.id];
            const wsSessions = sessionsByBrand.get(w.id) ?? [];
            return (
              <div
                key={w.id}
                data-testid={`workspace-folder-${w.id}`}
                className="rounded-lg"
              >
                <div
                  className={clsx(
                    'group flex items-center gap-1 rounded-lg transition-colors',
                    isActive ? 'bg-surface' : 'hover:bg-surface-hover',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => handleToggle(w.id)}
                    aria-label={isOpen ? `Collapse ${w.name}` : `Expand ${w.name}`}
                    aria-expanded={isOpen}
                    data-testid={`workspace-folder-toggle-${w.id}`}
                    className="p-1.5 text-muted-foreground hover:text-foreground cursor-pointer"
                  >
                    <IconChevronRight
                      className={clsx(
                        'w-3.5 h-3.5 transition-transform duration-200 ease-out motion-reduce:transition-none',
                        isOpen && 'rotate-90',
                      )}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelect(w.id)}
                    data-testid={`workspace-folder-select-${w.id}`}
                    className="flex-1 min-w-0 flex items-center gap-2 py-1.5 pr-1 text-left cursor-pointer"
                  >
                    <WorkspaceMarker workspace={w} />
                    <span
                      className={clsx(
                        'text-sm truncate',
                        isActive ? 'text-foreground font-medium' : 'text-foreground',
                      )}
                      title={w.name}
                    >
                      {w.name}
                    </span>
                  </button>
                  {canEditWorkspace && (
                    <button
                      type="button"
                      onClick={() => handleOpenSettings(w.id)}
                      aria-label={`${w.name} settings`}
                      data-testid={`workspace-folder-settings-${w.id}`}
                      // Hover-reveal on desktop keeps the row uncluttered, but
                      // touch devices have no hover - `max-md:opacity-100` forces
                      // the cog visible on the mobile drawer so the workspace
                      // stays editable there.
                      className="p-1 mr-1 text-muted-foreground hover:text-foreground opacity-0 max-md:opacity-100 group-hover:opacity-100 focus:opacity-100 transition-opacity cursor-pointer"
                    >
                      <IconSettings className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <span className="text-xs text-subtle-foreground shrink-0 mr-2">
                    {wsSessions.length}
                  </span>
                </div>

                <AnimatedPresence show={isOpen}>
                  {/* Keep folder spacing inside the collapsing region. */}
                  <div className="mt-0.5 mb-1 space-y-0.5">
                    {wsSessions.length === 0 ? (
                      <div
                        data-testid={`workspace-folder-empty-${w.id}`}
                        className="text-xs text-subtle-foreground px-2 py-1.5"
                      >
                        Nothing here yet.
                      </div>
                    ) : (
                      wsSessions.map((s) => {
                        const isSelected = currentSessionId === s.id;
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => onSessionClick?.(s.id)}
                            className={clsx(
                              'w-full text-left px-2 py-1.5 rounded-lg transition-colors group cursor-pointer',
                              isSelected
                                ? 'bg-surface-hover text-foreground'
                                : 'hover:bg-surface-hover',
                            )}
                          >
                            <div
                              className={clsx(
                                'text-sm truncate group-hover:text-foreground',
                                isSelected ? 'text-foreground' : 'text-foreground',
                              )}
                            >
                              <AnimatedLabel text={s.title} truncate />
                            </div>
                            <div className="text-xs text-subtle-foreground mt-0.5">
                              {s.date}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </AnimatedPresence>
              </div>
            );
          })
        )}

        {orphans.length > 0 && (
          <div className="mt-2 pt-2 border-t border-border">
            <div className="px-2 mb-1 text-xs uppercase tracking-wider text-subtle-foreground">
              Unassigned
            </div>
            {orphans.map((s) => {
              const isSelected = currentSessionId === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onSessionClick?.(s.id)}
                  className={clsx(
                    'w-full text-left p-2 rounded-lg transition-colors group cursor-pointer',
                    isSelected ? 'bg-surface' : 'hover:bg-surface-hover',
                  )}
                >
                  <div className="text-sm text-foreground group-hover:text-foreground truncate">
                    <AnimatedLabel text={s.title} truncate />
                  </div>
                  <div className="text-xs text-subtle-foreground mt-0.5">{s.date}</div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <CreateWorkspaceModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
};
